import React, { useState, useEffect } from 'react'
import { reduxForm, formValueSelector, change } from 'redux-form'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useFlags } from 'flags'

import {
  getTemplateBrandingState,
  templateIdIsBlankType,
  getDefaultTemplateIdForResponsive,
} from 'ducks/apps'
import { fetchOrganizations, getOrganizations } from 'ducks/organizations'
import { getCurrentUser } from 'ducks/users'

import { defaultBranding } from 'utils/colors'
import { createApp } from 'utils/io'
import { getNewAppWebSettings } from 'utils/webSettings'
import personalEmails from 'components/NewApp/personalEmails.json'
import { adaloBackendAxios } from 'utils/io/http/axios'

import Stepper from 'components/Shared/Stepper'
import WizardFormPlatformPage from './WizardFormPlatformPage'
import WizardFormBrandingPage from './WizardFormBrandingPage'
import WizardFormAdvancedPage from './WizardFormAdvancedPage'
import './NewApp.css'
import { FORM_NAME } from './shared'
import WizardFormTemplatesPage from './WizardFormTemplatesPage'
import WizardFormGoalPage from './WizardFormGoalPage'
import { GLEN_CALENDAR_URL, ADALO_EXPERTS_URL } from '../../constants'

const WizardFormStepper = ({ page }) => {
  switch (page) {
    case 'advanced':
      return null
    default:
      if (page === 0) {
        return null
      }

      return (
        <Stepper
          steps={['Platform', 'Template', 'Branding']}
          currentStep={page - 1}
        />
      )
  }
}

const WizardForm = ({
  fetchOrganizations,
  organizations,
  currentUser,
  datasourceType,
  templateId,
  change,
  branding,
  initialApp,
  personalEmail,
}) => {
  const { aiAssistant: aiAssistantFlag } = useFlags()
  const [page, setPage] = useState(aiAssistantFlag ? 0 : 1)

  useEffect(() => {
    fetchOrganizations()
  }, [fetchOrganizations])

  const handleNextPage = () => {
    setPage(prevPage => (typeof prevPage === 'number' ? prevPage + 1 : 1))
  }

  const handlePreviousPage = () => {
    setPage(prevPage => (typeof prevPage === 'number' ? prevPage - 1 : 1))
  }

  const handleOpenAdvanced = () => {
    setPage('advanced')
  }

  const handleSubmitAdvanced = ({ primaryPlatform }) => {
    if (!templateId) {
      change(
        FORM_NAME,
        'templateId',
        getDefaultTemplateIdForResponsive(primaryPlatform)
      )
    }

    if (datasourceType === 'database') {
      setPage(2)
    } else {
      if (!templateIdIsBlankType(templateId)) {
        change(
          FORM_NAME,
          'templateId',
          getDefaultTemplateIdForResponsive(primaryPlatform)
        )
      }
      setPage(3)
    }
  }

  const handleSubmitGoal = ({
    independent,
    established,
    businessName,
    website,
    type,
    users,
    description,
    timeline,
    budget,
  }) => {
    if (independent === false) {
      if (established && !personalEmail && budget > 2) {
        window.open(GLEN_CALENDAR_URL, '_blank')
      } else {
        window.open(ADALO_EXPERTS_URL, '_blank')

        if (
          process.env.NODE_ENV === 'production' &&
          established &&
          !personalEmail &&
          budget <= 2
        ) {
          adaloBackendAxios.post('/api/google-sheets/append', {
            businessName: String(businessName),
            website: String(website),
            type: String(type),
            users: String(users),
            description: String(description),
            timeline: String(timeline),
            budget: budget === 2 ? '$1,000 - $3,000' : '$0 - $1,000',
            email: currentUser.email,
          })
        }
      }
    }
    setPage(1)
  }

  const handleFinalSubmit = async ({
    primaryColor,
    secondaryColor,
    ...data
  }) => {
    data.datasourceAppId =
      data.datasourceType === 'copy' ? data.datasourceAppId : undefined

    data.branding = {
      ...defaultBranding,
      primary: primaryColor,
      secondary: secondaryColor,
    }

    if (!data.organizationId) {
      data.organizationId = organizations[0] && organizations[0].id
    }

    if (!data.organizationId) {
      return window.alert('Error: You are not part of any teams')
    }

    if (currentUser?.persona === 'Freelancer/Agency') {
      data.builtWithFreelancer = true
    }

    data.webSettings = getNewAppWebSettings({
      primaryPlatform: data.primaryPlatform,
      mobileOnly: data.primaryPlatform === 'mobile',
    })

    if (data.primaryPlatform === 'mobile') {
      data.primaryPlatform = 'responsive'
      data.webSettings = {
        layoutMode: 'mobile',
        ...data.webSettings,
      }
    } else {
      data.webSettings = {
        layoutMode: 'responsive',
        ...data.webSettings,
      }
    }

    data.useNewRNVersion = true
    data.useNewArchitecture = true

    if (initialApp && !data.independent) {
      data.persona = 'Hire Someone'
    }

    return createApp(data)
  }

  const renderPage = () => {
    const initialValues = {
      datasourceType: 'database',
      // TODO(enzo): why is magicLayout false by default?
      magicLayout: false,
      primaryColor: branding && branding.primary,
      secondaryColor: branding && branding.secondary,
    }

    switch (page) {
      case 0:
        return (
          <WizardFormGoalPage
            onSubmit={handleSubmitGoal}
            personalEmail={personalEmail}
          />
        )
      case 1:
        return (
          <WizardFormPlatformPage
            initialValues={initialValues}
            onSubmit={handleNextPage}
            handlePreviousPage={handlePreviousPage}
          />
        )
      case 2:
        return (
          <WizardFormTemplatesPage
            handlePreviousPage={handlePreviousPage}
            initialValues={initialValues}
            onSubmit={handleNextPage}
            onOpenAdvanced={handleOpenAdvanced}
          />
        )
      case 3:
        return (
          <WizardFormBrandingPage
            handlePreviousPage={handlePreviousPage}
            initialValues={initialValues}
            onSubmit={handleFinalSubmit}
          />
        )
      case 'advanced':
        return (
          <WizardFormAdvancedPage
            initialValues={initialValues}
            onSubmit={handleSubmitAdvanced}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <WizardFormStepper page={page} initialApp={initialApp} />
      {renderPage()}
      <WizardFormDestroyer />
    </>
  )
}

WizardForm.propTypes = {
  organizations: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchOrganizations: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  datasourceType: PropTypes.string,
  templateId: PropTypes.string,
  change: PropTypes.func.isRequired,
  branding: PropTypes.object,
}

const WizardFormDestroyer = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: true,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(() => null)

const mapStateToProps = state => {
  const selector = formValueSelector(FORM_NAME)
  const templateId = selector(state, 'templateId')
  const branding = templateIdIsBlankType(templateId)
    ? defaultBranding
    : getTemplateBrandingState(state, templateId).branding

  const user = getCurrentUser(state)
  const personalEmail = personalEmails.some(email =>
    user?.email.endsWith(email)
  )

  return {
    organizations: getOrganizations(state),
    templateId,
    branding,
    datasourceType: selector(state, 'datasourceType'),
    currentUser: getCurrentUser(state),
    personalEmail,
  }
}

const mapDispatchToProps = {
  fetchOrganizations,
  change,
}

export default connect(mapStateToProps, mapDispatchToProps)(WizardForm)
